import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ModalComponent} from '../../../core/components/modal/modal.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Photographer} from '../../../marketplace/entities/photographer/photographer.entity';
import {Service} from '../../../marketplace/entities/photographer/service.entity';
import {ReactiveFormsModule, FormBuilder, Validators, FormGroup} from '@angular/forms';
import {NgClass, CurrencyPipe} from '@angular/common';
import {DateTimePickerComponent, DateTimeSelection} from '../../../marketplace/components/date-time-picker/date-time-picker.component';
import {PhotographerService as PhotographerServiceCard} from '../../../marketplace/components/photographer/photographer-service/photographer-service';
import {BookingService} from '../../../marketplace/services/booking/booking.service';
import {DateTime} from 'luxon';
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {User} from '../../../core/entities/user/user.entity';
import {AnalyticsService} from '../../../core/services/analytics/analytics.service';
import {TrackingService} from '../../../core/services/tracking/tracking.service';
import {TrackingEventName} from '../../../core/entities/tracking/tracking-event.entity';

@Component({
  selector: 'app-photographer-book-now-modal',
  imports: [
    ModalComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    DateTimePickerComponent,
    PhotographerServiceCard,
    NgClass,
    CurrencyPipe
  ],
  templateUrl: './photographer-book-now-modal.html',
  styleUrl: './photographer-book-now-modal.scss',
})
export class PhotographerBookNowModal {
    @Input() isOpen: boolean = false;
    @Input() photographer: Photographer|null = null;
    @Output() onClose = new EventEmitter();

    public form: FormGroup;
    public selectedService: Service|null = null;
    public selectedDateTime: DateTime|null = null;
    public isSubmitting = false;
    public locale: string = 'en';
    private readonly user: User|null = null;
    private emailBlurred = false;
    private readonly sectionOrder: SectionKey[] = ['service', 'datetime', 'customer'];
  public sectionState: Record<SectionKey, boolean> = {
    service: true,
    datetime: false,
    customer: false,
  };
  private noteBlurred = false;
  private hasTrackedServiceSelection = false;
  private hasTrackedSlotSelection = false;
  private hasTrackedStartCheckout = false;

    public constructor(
      private readonly fb: FormBuilder,
      private readonly bookingService: BookingService,
      private readonly transloco: TranslocoService,
      private readonly toastr: ToastrService,
      private readonly authenticationService: AuthenticationService,
      private readonly analytics: AnalyticsService,
      private readonly tracking: TrackingService,
    ) {
      this.user = this.authenticationService.getCurrentUser();
      this.locale = this.transloco.getActiveLang();
      this.form = this.fb.group({
        serviceId: [null, Validators.required],
        datetime: [null, Validators.required],
        firstname: [this.user?.firstname ?? '', Validators.required],
        lastname: [this.user?.lastname ?? '', Validators.required],
        email: [this.user?.email ?? '', [Validators.required, Validators.email]],
        note: ['', Validators.required],
        termsAccepted: [false, Validators.requiredTrue],
      });

      this.form.statusChanges.subscribe(() => this.syncAutoClose());
    }

    public close(): void {
      this.resetForm();
      this.onClose.emit();
    }

    public onSelectService(service: Service): void {
      this.selectedService = service;
      this.form.patchValue({ serviceId: service.id });
      this.resetDateSelection();
      this.openNextSection('service');
      this.trackServiceSelection(service);
      this.syncAutoClose();
    }

    public onDateSelected(_date: string): void {
      this.resetDateSelection();
    }

    public onDateTimeSelection(selection: DateTimeSelection): void {
      this.selectedDateTime = selection.dateTime;
      this.form.patchValue({ datetime: selection.dateTime });
      this.openNextSection('datetime');
      this.trackSlotSelection(selection);
      this.syncAutoClose();
    }

    public get selectedDateLabel(): string | null {
      if (!this.selectedDateTime) return null;
      return this.selectedDateTime.setLocale(this.locale).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
    }

    public get selectedTimeLabel(): string | null {
      if (!this.selectedDateTime) return null;
      return this.selectedDateTime.setLocale(this.locale).toLocaleString(DateTime.TIME_SIMPLE);
    }

    public submit(): void {
      if (!this.selectedService || !this.selectedDateTime || this.form.invalid) {
        this.form.markAllAsTouched();
        this.toastr.error(
          this.transloco.translate('marketplace.booking.create.select-date-form.errors.content'),
          this.transloco.translate('marketplace.booking.create.select-date-form.errors.title')
        );
        return;
      }

      const { firstname, lastname, email, note } = this.form.getRawValue();
      const price = this.getDisplayPrice(this.selectedService);

      this.trackStartCheckout(price);

      this.isSubmitting = true;
      this.bookingService.createBooking(
        this.selectedService.id,
        email,
        firstname,
        lastname,
        price,
        note ?? null,
        this.selectedDateTime,
        null
      ).subscribe({
        next: (result) => {
          this.isSubmitting = false;
          if (typeof window !== 'undefined') {
            window.location.href = result.checkoutUrl;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error(
            this.transloco.translate('marketplace.booking.create.select-date-form.errors.content'),
            this.transloco.translate('marketplace.booking.create.select-date-form.errors.title')
          );
        }
      });
    }

    public get isSubmitDisabled(): boolean {
      return this.isSubmitting || !this.selectedService || !this.selectedDateTime || this.form.invalid;
    }

    public getDisplayPrice(service: Service|null): number|null {
      if (!service) return null;
      const base = service.priceRangeActive
        ? (service.priceRangeMinPublic ?? service.priceRangeMaxPublic ?? null)
        : (service.publicPrice ?? service.price ?? null);

      if (base === null) {
        return null;
      }

      if (service.discountActive && service.discount) {
        return base * (1 - service.discount / 100);
      }

      return base;
    }

    private resetDateSelection(): void {
      this.selectedDateTime = null;
      this.form.patchValue({ datetime: null });
    }

    private resetForm(): void {
      this.form.reset({
        serviceId: null,
        datetime: null,
        firstname: this.user?.firstname ?? '',
        lastname: this.user?.lastname ?? '',
        email: this.user?.email ?? '',
        note: '',
        termsAccepted: false,
      });
      this.selectedService = null;
      this.selectedDateTime = null;
      this.isSubmitting = false;
      this.sectionState = { service: true, datetime: false, customer: false };
      this.emailBlurred = false;
      this.noteBlurred = false;
      this.hasTrackedServiceSelection = false;
      this.hasTrackedSlotSelection = false;
      this.hasTrackedStartCheckout = false;
    }

    public isSectionOpen(section: SectionKey): boolean {
      return this.sectionState[section];
    }

    public isSectionComplete(section: SectionKey): boolean {
      if (section === 'service') {
        return !!this.selectedService;
      }
      if (section === 'datetime') {
        return !!this.selectedDateTime;
      }
      // customer
      const firstnameValid = this.form.get('firstname')?.valid ?? false;
      const lastnameValid = this.form.get('lastname')?.valid ?? false;
      const emailValid = (this.form.get('email')?.valid ?? false) && this.emailBlurred;
      const noteValid = (this.form.get('note')?.valid ?? false) && this.noteBlurred;
      const termsValid = this.form.get('termsAccepted')?.valid ?? false;
      return firstnameValid && lastnameValid && emailValid && noteValid && termsValid;
    }

    public toggleSection(section: SectionKey): void {
      const willOpen = !this.sectionState[section];
      this.sectionState = {
        service: false,
        datetime: false,
        customer: false,
      };
      if (willOpen) {
        this.sectionState[section] = true;
      }
    }

    private openNextSection(current: SectionKey): void {
      const currentIndex = this.sectionOrder.indexOf(current);
      const nextSection = this.sectionOrder[currentIndex + 1] ?? null;
      if (nextSection) {
        this.sectionState = {
          service: false,
          datetime: false,
          customer: false,
        };
        this.sectionState[nextSection] = true;
      }
    }

    private closeAllSections(): void {
      this.sectionState = {
        service: false,
        datetime: false,
        customer: false,
      };
    }

    private syncAutoClose(): void {
      if (this.selectedService && this.selectedDateTime && this.isSectionComplete('customer')) {
        this.closeAllSections();
      }
    }

    public onEmailFocus(): void {
      this.emailBlurred = false;
    }

    public onEmailBlur(): void {
      this.emailBlurred = true;
      this.syncAutoClose();
    }

    public onNoteFocus(): void {
      this.noteBlurred = false;
    }

    public onNoteBlur(): void {
      this.noteBlurred = true;
      this.syncAutoClose();
    }

    private trackServiceSelection(service: Service): void {
      if (this.hasTrackedServiceSelection || !this.photographer) return;
      const payload = {
        service_id: service.id,
        service_name: service.name,
        photographer_id: this.photographer.id,
        photographer_slug: this.photographer.slug,
        source: 'book_now_modal',
      };
      this.analytics.event('select_service', payload);
      this.tracking.track(TrackingEventName.PHOTOGRAPHER_MODAL_SERVICE_SELECTED, payload, {
        gaEventName: 'photographer_modal_service_selected',
        flow: 'photographer_modal',
      });
      this.hasTrackedServiceSelection = true;
    }

    private trackSlotSelection(selection: DateTimeSelection): void {
      if (this.hasTrackedSlotSelection || !this.selectedService || !this.photographer) return;
      const datetime = selection.dateTime.toUTC().toISO();
      if (!datetime) return;
      const payload = {
        service_id: this.selectedService.id,
        service_name: this.selectedService.name,
        photographer_id: this.photographer.id,
        photographer_slug: this.photographer.slug,
        datetime,
        source: 'book_now_modal',
      };
      this.analytics.event('select_slot', payload);
      this.tracking.track(TrackingEventName.PHOTOGRAPHER_MODAL_TIMESLOT_SELECTED, payload, {
        gaEventName: 'photographer_modal_timeslot_selected',
        flow: 'photographer_modal',
      });
      this.hasTrackedSlotSelection = true;
    }

    private trackStartCheckout(price: number|null): void {
      if (this.hasTrackedStartCheckout || !this.selectedService || !this.selectedDateTime || !this.photographer) return;
      const datetime = this.selectedDateTime.toUTC().toISO();
      if (!datetime) return;
      const paymentMode: 'full' | 'deposit' = 'full';
      const payload = {
        service_id: this.selectedService.id,
        service_name: this.selectedService.name,
        photographer_id: this.photographer.id,
        photographer_slug: this.photographer.slug,
        datetime,
        price,
        payment_mode: paymentMode,
        deposit_percentage: null,
        currency: 'EUR',
        source: 'book_now_modal',
      };
      this.analytics.event('start_checkout', payload);
      this.tracking.track(TrackingEventName.PHOTOGRAPHER_MODAL_DETAILS_SUBMITTED, payload, {
        gaEventName: 'photographer_modal_details_submitted',
        flow: 'photographer_modal',
        rememberFlow: true,
      });
      this.tracking.track(TrackingEventName.CHECKOUT_STARTED, payload, {
        gaEventName: 'checkout_started',
        idempotencyKey: this.photographer?.id ? `photographer:${this.photographer.id}:CHECKOUT:${datetime}` : undefined,
        flow: 'photographer_modal',
        rememberFlow: true,
      });
      this.hasTrackedStartCheckout = true;
    }
}

type SectionKey = 'service' | 'datetime' | 'customer';
